import Autocomplete, { type AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase';
import type { Option } from '@openx/types/options';
import { Fragment, type SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SearchAndSelectFilterProps = {
  isLoading?: boolean;
  noOptionsText?: string;
  onInputChange: (value: string) => void;
  onOptionChange: (optionId: string) => void;
  options: Option[];
  selectedOption: string;
};

export const SearchAndSelectFilter = ({
  isLoading,
  noOptionsText,
  onInputChange,
  onOptionChange,
  options,
  selectedOption,
}: SearchAndSelectFilterProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [value, setValue] = useState<Option | null>(getInitialValue(options, selectedOption));
  const { t } = useTranslation();

  const handleChange = useCallback(
    (_: SyntheticEvent, option: Option | null) => {
      setValue(option);
      onOptionChange(option?.id ?? '');
    },
    [onOptionChange]
  );

  const handleInputChange = useCallback(
    (_: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'clear' || reason === 'input' || reason === 'reset') {
        setInputValue(value);
        onInputChange(value);
      }
    },
    [onInputChange]
  );

  return (
    <Autocomplete
      getOptionLabel={option => option.name}
      loading={isLoading}
      onChange={handleChange}
      noOptionsText={isLoading ? t('Loading ...') : noOptionsText}
      onInputChange={handleInputChange}
      options={options}
      renderOption={({ key, ...props }, option, _, ownerState) => {
        const label = ownerState.getOptionLabel(option);

        return (
          <Tooltip key={key} title={<HighlightedPhrase searchPhrase={inputValue}>{label}</HighlightedPhrase>}>
            <Typography {...props} component="li">
              <HighlightedPhrase
                searchPhrase={inputValue}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {label}
              </HighlightedPhrase>
            </Typography>
          </Tooltip>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={t('[ Select or search ]')}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </Fragment>
              ),
              type: 'search',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              paddingBlock: 0.25,
              paddingInline: 0.5,
            },
          }}
          variant="outlined"
        />
      )}
      value={value}
    />
  );
};

const getInitialValue = (options: Option[] | undefined, selected: string | undefined) => {
  if (!selected) {
    return null;
  }

  return options?.find(({ id }) => id === selected) ?? { id: selected, name: selected };
};
